import React from 'react';
import {Document, Page, pdfjs} from 'react-pdf/dist/umd/entry.webpack'
import "../config/styles.css"
import Draggable from 'react-draggable';
import DrawingOptions from './DrawingOptions';
import { SERVER_API_URL } from '../config/Constants'
import Utils from '../utils/utils'
import fetchClient from "../utils/http-common";

const MAX_SVG_SIZE = 3000;

export default class DrawingArea extends React.Component {
  constructor(props) {
    super(props);
    this.svgRef = React.createRef();

    this.state = {
      color: 'rgb(255, 255, 0)',
      erasing: false,
      brushSize: 15,
      transparent: true,
      paths: []
    }

    this.drawingObjectOnServer = null;
    this.lastPositionX = -42;
    this.lastPositionY = -42;
    this.isDrawing = false;
    this.currentPath = null;
    this.hasDrawn = false;
    this.svgContentOnServer = null;
    this.saveIfNeededIn10s();
  }

  componentDidUpdate(prevProps) {
    if (this.props.pdfWidth !== prevProps.pdfWidth || this.props.pdfHeight !== prevProps.pdfHeight) {
      console.log("pdfWidth or pdfHeight changed", this.props.pdfWidth, prevProps.pdfWidth, this.props.pdfHeight, prevProps.pdfHeight)
      this.sizeChanged();
    }
    if (this.props.enabled !== prevProps.enabled) {
      console.log("enabled changed", this.props.enabled)
      if (this.hasDrawn) {
        this.updateDrawingOnServer();
      }
    }
  }

  componentWillUnmount() {
    if (this.timerSave) {
      clearTimeout(this.timerSave);
    }
    if (this.hasDrawn) {
      this.updateDrawingOnServer();
    }
  }

  resetSaveTimer() {
    if (this.timerSave) {
      clearTimeout(this.timerSave);
    }
    this.timerSave = setTimeout(() => this.saveIfNeededIn10s(), 10000); // 10s
  }

  saveIfNeededIn10s() {
    if (this.hasDrawn) {
      this.updateDrawingOnServer();
    }
    this.resetSaveTimer();
  }

  sizeChanged() {
    const svg = this.svgRef.current;
    if (!svg || typeof svg.setAttribute !== 'function') return;

    var svgWidth = this.props.pdfWidth > this.props.pdfHeight ? MAX_SVG_SIZE : this.props.pdfWidth * MAX_SVG_SIZE / this.props.pdfHeight;
    var svgHeight = this.props.pdfHeight > this.props.pdfWidth ? MAX_SVG_SIZE : this.props.pdfHeight * MAX_SVG_SIZE / this.props.pdfWidth;

    svg.setAttribute('width', this.props.pdfWidth);
    svg.setAttribute('height', this.props.pdfHeight);
    svg.setAttribute('viewBox', `0 0 ${svgWidth} ${svgHeight}`);

    this.svgScaleWidth = svgWidth / this.props.pdfWidth;
    this.svgScaleHeight = svgHeight / this.props.pdfHeight;

    if (this.svgContentOnServer) {
      this.loadSvg();
    }
  }

  componentDidMount() {
    this.loadDrawingFromServer();

    const svg = this.svgRef.current;
    if (!svg) return;

    svg.addEventListener("mousedown", (e) => {
      if (!this.props.enabled) return;
      if (this.state.isErasing) return;
      
      this.isDrawing = true;
      const point = this.getMousePosition(e);
      this.startDrawing(point);
    });

    svg.addEventListener("mousemove", (e) => {
      if (!this.props.enabled) return;
      
      if (this.isDrawing) {
        const point = this.getMousePosition(e);
        this.draw(point);
        this.resetSaveTimer();
      } else if (this.state.isErasing) {
        const point = this.getMousePosition(e);
        this.eraseAtPoint(point);
        this.resetSaveTimer();
      }
    });

    svg.addEventListener("mouseup", () => {
      if (this.isDrawing) {
        this.endDrawing();
      }
    });

    svg.addEventListener("mouseleave", () => {
      if (this.isDrawing) {
        this.endDrawing();
      }
    });

    // Touch events
    svg.addEventListener("touchstart", (e) => {
      e.preventDefault();
      if (!this.props.enabled) return;
      if (this.state.isErasing) return;
      
      this.isDrawing = true;
      const touch = e.touches[0];
      const point = this.getTouchPosition(touch);
      this.startDrawing(point);
    });

    svg.addEventListener("touchmove", (e) => {
      e.preventDefault();
      if (!this.props.enabled) return;
      
      const touch = e.touches[0];
      if (this.isDrawing) {
        const point = this.getTouchPosition(touch);
        this.draw(point);
        this.resetSaveTimer();
      } else if (this.state.isErasing) {
        const point = this.getTouchPosition(touch);
        this.eraseAtPoint(point);
        this.resetSaveTimer();
      }
    });

    svg.addEventListener("touchend", (e) => {
      e.preventDefault();
      if (this.isDrawing) {
        this.endDrawing();
      }
    });
  }

  getMousePosition(evt) {
    const svg = this.svgRef.current;
    const CTM = svg.getScreenCTM();
    return {
      x: (evt.clientX - CTM.e) / CTM.a,
      y: (evt.clientY - CTM.f) / CTM.d
    };
  }

  getTouchPosition(touch) {
    const svg = this.svgRef.current;
    const CTM = svg.getScreenCTM();
    return {
      x: (touch.clientX - CTM.e) / CTM.a,
      y: (touch.clientY - CTM.f) / CTM.d
    };
  }

  startDrawing(point) {
    const svg = this.svgRef.current;
    this.currentPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    
    const opacity = this.state.transparent ? 0.4 : 1;
    
    this.currentPath.setAttribute('stroke', this.state.color);
    this.currentPath.setAttribute('stroke-width', this.state.brushSize);
    this.currentPath.setAttribute('stroke-opacity', opacity);
    this.currentPath.setAttribute('fill', 'none');
    this.currentPath.setAttribute('stroke-linecap', 'round');
    this.currentPath.setAttribute('stroke-linejoin', 'round');
    
    const d = `M ${point.x} ${point.y}`;
    this.currentPath.setAttribute('d', d);
    
    svg.appendChild(this.currentPath);
    this.hasDrawn = true;
  }

  draw(point) {
    if (!this.currentPath) return;
    
    const d = this.currentPath.getAttribute('d') + ` L ${point.x} ${point.y}`;
    this.currentPath.setAttribute('d', d);
  }

  endDrawing() {
    if (this.currentPath) {
      const newPaths = [...this.state.paths, this.currentPath];
      this.setState({ paths: newPaths });
      this.currentPath = null;
    }
    this.isDrawing = false;
  }

  eraseAtPoint(point) {
    const svg = this.svgRef.current;
    const pathElements = svg.querySelectorAll('path');
    const pathsArray = Array.from(pathElements);
    
    for (let i = 0; i < pathsArray.length; i++) {
      const path = pathsArray[i];
      const totalLength = path.getTotalLength();
      const gapLength = this.state.brushSize * 2; // Erase size based on brush size
      const strokeWidth = parseInt(path.getAttribute('stroke-width'));
      const threshold = strokeWidth / 2 + 5;
      let hit = false;
      let intersectionLength = 0;
      const step = 1;

      for (let l = 0; l <= totalLength; l += step) {
        const pt = path.getPointAtLength(l);
        const dist = Math.hypot(pt.x - point.x, pt.y - point.y);
        if (dist < threshold) {
          hit = true;
          intersectionLength = l;
          break;
        }
      }

      if (hit) {
        // Get the original path data
        const originalPathData = path.getAttribute('d');
        
        // Find the segments before and after the intersection point
        const startL = Math.max(0, intersectionLength - gapLength / 2);
        const endL = Math.min(totalLength, intersectionLength + gapLength / 2);
        
        // Create two new path segments
        const firstSegment = this.getPathSegmentUpToLength(path, startL, totalLength);
        const secondSegment = this.getPathSegmentAfterLength(path, endL, totalLength);
        
        // Remove the original path
        svg.removeChild(path);
        
        // Update the state paths array
        const newPaths = this.state.paths.filter(p => p !== path);
        
        // Add the new paths if they have data
        if (firstSegment) {
          const newPath1 = document.createElementNS('http://www.w3.org/2000/svg', 'path');
          newPath1.setAttribute('stroke', path.getAttribute('stroke'));
          newPath1.setAttribute('stroke-width', path.getAttribute('stroke-width'));
          newPath1.setAttribute('stroke-opacity', path.getAttribute('stroke-opacity'));
          newPath1.setAttribute('fill', 'none');
          newPath1.setAttribute('stroke-linecap', 'round');
          newPath1.setAttribute('stroke-linejoin', 'round');
          newPath1.setAttribute('d', firstSegment);
          svg.appendChild(newPath1);
          newPaths.push(newPath1);
        }
        
        if (secondSegment) {
          const newPath2 = document.createElementNS('http://www.w3.org/2000/svg', 'path');
          newPath2.setAttribute('stroke', path.getAttribute('stroke'));
          newPath2.setAttribute('stroke-width', path.getAttribute('stroke-width'));
          newPath2.setAttribute('stroke-opacity', path.getAttribute('stroke-opacity'));
          newPath2.setAttribute('fill', 'none');
          newPath2.setAttribute('stroke-linecap', 'round');
          newPath2.setAttribute('stroke-linejoin', 'round');
          newPath2.setAttribute('d', secondSegment);
          svg.appendChild(newPath2);
          newPaths.push(newPath2);
        }
        
        this.setState({ paths: newPaths });
        this.hasDrawn = true;
        break;
      }
    }
  }

  // Helper function to get path segment up to a specific length
  getPathSegmentUpToLength(pathElement, targetLength, totalLength) {
    if (targetLength <= 0) return '';
    
    let d = '';
    const startPoint = pathElement.getPointAtLength(0);
    d += `M ${startPoint.x} ${startPoint.y} `;
    
    // Use a smaller step for better accuracy
    const step = Math.min(1, totalLength / 300);
    for (let l = step; l < targetLength; l += step) {
      const pt = pathElement.getPointAtLength(l);
      d += `L ${pt.x} ${pt.y} `;
    }
    
    // Add exact target point
    const endPoint = pathElement.getPointAtLength(targetLength);
    d += `L ${endPoint.x} ${endPoint.y}`;
    
    return d;
  }

  // Helper function to get path segment after a specific length
  getPathSegmentAfterLength(pathElement, startLength, totalLength) {
    if (startLength >= totalLength) return '';
    
    let d = '';
    const startPoint = pathElement.getPointAtLength(startLength);
    d += `M ${startPoint.x} ${startPoint.y} `;
    
    const step = Math.min(1, totalLength / 300);
    for (let l = startLength + step; l < totalLength; l += step) {
      const pt = pathElement.getPointAtLength(l);
      d += `L ${pt.x} ${pt.y} `;
    }
    
    // Add exact end point
    const endPoint = pathElement.getPointAtLength(totalLength);
    d += `L ${endPoint.x} ${endPoint.y}`;
    
    return d;
  }

  async loadDrawingFromServer() {
    var params = {
      "user_project_id": this.props.userProjectId,
      "pageNb": this.props.pageNb - 1,
    }

    var result = await fetchClient.post(`${SERVER_API_URL}/page_drawings/getDrawingForPage.json`, params)

    if (result.data) {
      this.drawingObjectOnServer = result.data
      if (result.data.imgUrlOnServer) {
        this.imgUrlOnServer = result.data.imgUrlOnServer + "?" + Date.now()
        this.loadSvgContentFromUrl(result.data.imgUrlOnServer);
      }
    }
  }

  async loadSvgContentFromUrl(url) {
    try {
      const response = await fetch(url);
      if (response.ok) {
        const svgContent = await response.text();
        this.svgContentOnServer = svgContent; // Store in a separate variable
        this.loadSvg();
      } else {
        console.error('Failed to fetch SVG content from URL');
      }
    } catch (error) {
      console.error('Error fetching SVG content:', error);
    }
  }
  

  loadSvg() {
    if (this.props.pdfWidth < 200 || this.props.pdfHeight < 200) {
      console.log("pdfWidth or pdfHeight too small")
      return
    }
    const svg = this.svgRef.current;
    if (!svg) return;

    // Clear existing content
    while (svg.firstChild) {
      svg.removeChild(svg.firstChild);
    }
   // alert(this.svgContentOnServer)

    // Parse and add the SVG content
    const parser = new DOMParser();
    const parsedSvg = parser.parseFromString(this.svgContentOnServer, "image/svg+xml");
    const svgElement = parsedSvg.documentElement;
    const originalViewBox = svgElement.getAttribute('viewBox');

    if (originalViewBox) {
      svg.setAttribute('viewBox', originalViewBox);
    }
    const paths = parsedSvg.getElementsByTagName('path');
    
    const newPaths = [];
for (let i = 0; i < paths.length; i++) {
  const path = paths[i];
  const newPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  
  // Copy all attributes except 'd'
  for (let j = 0; j < path.attributes.length; j++) {
    const attr = path.attributes[j];
    if (attr.name !== 'd') {
      newPath.setAttribute(attr.name, attr.value);
    }
  }
  
  // For the 'd' attribute, you may need to transform coordinates
  // if the viewBox is different
  newPath.setAttribute('d', path.getAttribute('d'));
  
  svg.appendChild(newPath);
  newPaths.push(newPath);
}
    
    this.setState({ paths: newPaths });
  }

  async updateDrawingOnServer() {
    var drawing = {
      "pageNb": this.props.pageNb - 1,
      "projectPatternUniqueId": this.props.projectPatternUniqueIdOnClient,
      "platformOrig": "web",
      "deleted": 0,
      "updatedAtOnClient": Date.now(),
      "support": 0,
      "version": this.drawingObjectOnServer ? this.drawingObjectOnServer.version + 1 : 1,
      "uniqueIdOnClient": this.drawingObjectOnServer ? this.drawingObjectOnServer.uniqueIdOnClient : Utils.getUID()
    }

    var result;
    if (this.drawingObjectOnServer && this.drawingObjectOnServer.id)
      result = await fetchClient.put(`${SERVER_API_URL}/page_drawings/${this.drawingObjectOnServer.id}.json`, drawing)
    else
      result = await fetchClient.post(`${SERVER_API_URL}/page_drawings.json`, drawing)

    this.drawingObjectOnServer = result.data
    await this.uploadSvgContent();
  }

  async uploadSvgContent() {
    const svg = this.svgRef.current;
    this.hasDrawn = false;

    // Clone the SVG to avoid modifying the original
    const clone = svg.cloneNode(true);
    
    // Serialize the SVG XML
    const serializer = new XMLSerializer();
    const svgContent = serializer.serializeToString(clone);

    const formData = new FormData();
    //add the svg as a file
    var blob = new Blob([svgContent], {type: "image/svg+xml"});
    formData.append('image', blob);

    //formData.append('svgContent', svgContent);

    // POST request to server
    try {
      const response = await fetchClient.post(`${SERVER_API_URL}/page_drawings/${this.drawingObjectOnServer.id}/addImg.json`, formData);
      this.svgContentOnServer = response.data.svgContent;
      console.log("SVG saved successfully");
    } catch (error) {
      console.log("Error saving SVG:", error);
    }
  }

  render() {
    return (
      <div style={{ position: 'absolute', top: 0, left: 0, zIndex: 10, width: '100%', height: '100%'}}>
        <svg 
          ref={this.svgRef} 
          style={{ position: 'absolute', top: 0, left: 0, zIndex: 10, width: '100%', height: '100%'}} 
          width={this.props.pdfWidth}
          height={this.props.pdfHeight}
        />
        {this.props.enabled &&
          <DrawingOptions
            setErasing={(erasing) => {
              this.setState({isErasing: erasing});
            }}

            setColor={(color) => {
              this.setState({color: color, isErasing: false});
            }}
            
            setTransparent={(transparent) => {
              this.setState({transparent: transparent});
            }}

            setBrushSize={(size) => {
              this.setState({brushSize: size});
            }}
            
            onSave={() => {
              this.updateDrawingOnServer();
            }}
          />
        }
      </div>
    );
  }
}
